import { Select } from "@omnitok/ui";
import { useEffect, useRef, useState } from "react";
import type {
  DatePreset,
  DatePresetCompute,
} from "@pervasivemind/dashboard-render";

import { useT } from "@/i18n";
import { previewApi, type DatePresetPreviewResponse } from "@/lib/builder-api";
import { BilingualInput } from "@/pages/builder/components/BilingualInput";

interface DatePresetEditorProps {
  /** Current preset list (read from ``filter.presets``). */
  presets: DatePreset[];
  /** Optional default-value preset id (for the "⚠ used as default" badge). */
  defaultPresetId?: string | null;
  /** Filter timezone — forwarded to the live-preview endpoint. */
  timezone?: string | null;
  /** Called whenever the list changes. */
  onChange: (next: DatePreset[]) => void;
}

/**
 * Iter 26 — editor for the per-filter ``presets[]`` list. Each preset
 * is rendered as a card with id / bilingual label / description /
 * kind dropdown / kind-specific params, plus a live preview ("Resolves
 * to: 2025-05-01 → 2026-05-26") via the
 * ``POST /api/builder/preview/date-preset`` endpoint.
 *
 * Add / delete / reorder via small buttons on each card. NO defaults
 * for missing params (per user rule: fail loud) — when the admin
 * picks a new kind the UI offers sensible starter values they must
 * verify before saving.
 */
/** Monotonic uid source for preset-card React keys. */
let presetCardUidCounter = 0;
function makePresetCardUid(): string {
  presetCardUidCounter += 1;
  return `preset-card-${presetCardUidCounter}`;
}

export function DatePresetEditor({
  presets,
  defaultPresetId,
  timezone,
  onChange,
}: DatePresetEditorProps): JSX.Element {
  const { t } = useT();

  // Stable per-card identity for React keys. Keying on
  // ``${preset.id}-${idx}`` remounted the card on EVERY keystroke in the
  // id input (focus lost, local card state — seen/resolved preview —
  // dropped). The uids live in a ref, parallel to ``presets``, and follow
  // every STRUCTURAL edit (add / delete / move) below; content edits
  // never touch them. They are deliberately NOT derived from editable
  // content. If the parent swaps the array wholesale (a different filter
  // selected), the lengths diverge and we re-seed — a full remount is the
  // correct behaviour there.
  const uidsRef = useRef<string[]>([]);
  if (uidsRef.current.length !== presets.length) {
    uidsRef.current = presets.map(
      (_, i) => uidsRef.current[i] ?? makePresetCardUid(),
    );
  }

  const addNew = (): void => {
    // Sensible starter compute the admin will customise. NOT a fallback —
    // it's an editable seed value visible in the UI.
    const newId = `preset_${presets.length + 1}`;
    const newPreset: DatePreset = {
      id: newId,
      label: { en: "New preset", es: "Preset nuevo" },
      description: null,
      compute: {
        kind: "absolute_range",
        params: { start: "today", end: "tomorrow" },
      },
    };
    uidsRef.current = [...uidsRef.current, makePresetCardUid()];
    onChange([...presets, newPreset]);
  };

  const updateAt = (idx: number, patch: Partial<DatePreset>): void => {
    const next = [...presets];
    next[idx] = { ...next[idx], ...patch } as DatePreset;
    onChange(next);
  };

  const deleteAt = (idx: number): void => {
    uidsRef.current = uidsRef.current.filter((_, i) => i !== idx);
    onChange(presets.filter((_, i) => i !== idx));
  };

  const move = (from: number, to: number): void => {
    if (to < 0 || to >= presets.length) return;
    const next = [...presets];
    const [item] = next.splice(from, 1);
    if (!item) return;
    next.splice(to, 0, item);
    const nextUids = [...uidsRef.current];
    const [uid] = nextUids.splice(from, 1);
    if (uid) nextUids.splice(to, 0, uid);
    uidsRef.current = nextUids;
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-neutral-600">
          {t("editor.preset.title")}
        </span>
        <button
          type="button"
          onClick={addNew}
          className="rounded bg-primary px-2 py-0.5 text-xs font-bold text-white hover:bg-primary-dark"
        >
          {t("editor.preset.add")}
        </button>
      </div>
      {presets.length === 0 && (
        <p className="text-xs italic text-neutral-500">
          {t("editor.preset.empty")}
        </p>
      )}
      <ul className="space-y-2">
        {presets.map((preset, idx) => (
          <PresetCard
            key={uidsRef.current[idx]}
            preset={preset}
            isDefault={preset.id === defaultPresetId}
            timezone={timezone}
            canMoveUp={idx > 0}
            canMoveDown={idx < presets.length - 1}
            onUpdate={(patch) => updateAt(idx, patch)}
            onDelete={() => deleteAt(idx)}
            onMoveUp={() => move(idx, idx - 1)}
            onMoveDown={() => move(idx, idx + 1)}
          />
        ))}
      </ul>
    </div>
  );
}

interface PresetCardProps {
  preset: DatePreset;
  isDefault: boolean;
  timezone?: string | null;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onUpdate: (patch: Partial<DatePreset>) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function PresetCard({
  preset,
  isDefault,
  timezone,
  canMoveUp,
  canMoveDown,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
}: PresetCardProps): JSX.Element {
  const { t } = useT();
  const cardRef = useRef<HTMLLIElement>(null);
  const [resolved, setResolved] = useState<DatePresetPreviewResponse | null>(
    null,
  );
  const [resolveError, setResolveError] = useState<string | null>(null);
  // Defer the live-preview fetch until the card is actually on screen. The
  // root inspector mounts EVERY global-filter card at once, so without this
  // gate every preset card of every date_range filter would POST
  // /preview/date-preset on mount — N filters × M presets round-trips the
  // admin may never scroll to. IntersectionObserver fires the fetch only
  // once the card enters the viewport; cards already visible resolve
  // immediately (no perceptible change). Sticky: once seen, later
  // compute/timezone edits keep refreshing the preview live.
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    if (seen) return;
    const el = cardRef.current;
    // No IntersectionObserver (jsdom / very old browser) → resolve eagerly
    // so the preview is never silently missing. Capability detection, not a
    // data fallback: behaviour degrades to exactly the previous eager fetch.
    if (!el || typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setSeen(true);
        obs.disconnect();
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [seen]);

  // Live preview: resolve the compute via the builder API once the card is
  // visible, and again whenever compute/timezone changes. Debounced 300ms
  // so typing in number inputs doesn't spam the endpoint. ``previewApi``
  // carries the JWT (stored per tenant under ``builder.token:{tenant}``,
  // see ``tokenKeyFor`` in ``lib/tenant-path.ts``) + 401 handling — no
  // hand-rolled localStorage token scan. NB: a 401 here triggers the
  // builder-WIDE ``builder:auth-expired`` logout (api.ts convention: any
  // expired-token response signs the admin out) — intentional, this
  // per-card preview is no exception. The response shape is validated in
  // ``previewApi.datePreset`` (throws on anything but {start, end, days}),
  // so ``resolved`` is never a malformed body.
  useEffect(() => {
    if (!seen) return;
    const controller = new AbortController();
    const handle = setTimeout(() => {
      previewApi
        .datePreset(
          { compute: preset.compute, ...(timezone ? { tz: timezone } : {}) },
          { signal: controller.signal },
        )
        .then((j) => {
          setResolved(j);
          setResolveError(null);
        })
        .catch((e: unknown) => {
          if (e instanceof DOMException && e.name === "AbortError") return;
          setResolved(null);
          setResolveError(
            e instanceof Error ? e.message.slice(0, 100) : "Preview failed",
          );
        });
    }, 300);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [preset.compute, timezone, seen]);

  const updateCompute = (compute: DatePresetCompute): void => {
    onUpdate({ compute });
  };

  return (
    <li
      ref={cardRef}
      className="rounded-md border border-neutral-200 bg-neutral-50 p-2"
    >
      <div className="mb-1 flex items-center gap-1.5">
        <input
          value={preset.id}
          onChange={(e) =>
            onUpdate({ id: e.target.value.replace(/[^a-z0-9_]/gi, "_") })
          }
          className="flex-1 rounded border border-neutral-300 bg-white px-1.5 py-0.5 font-mono text-xs"
          placeholder="preset_id"
        />
        {isDefault && (
          <span
            className="rounded bg-warning/20 px-1.5 py-0.5 text-xs text-warning-dark"
            title={t("editor.preset.defaultHint")}
          >
            {t("editor.preset.default")}
          </span>
        )}
        <button
          type="button"
          onClick={onMoveUp}
          disabled={!canMoveUp}
          className="text-neutral-500 hover:text-neutral-700 disabled:opacity-30"
          title={t("editor.preset.moveUp")}
        >
          ↑
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={!canMoveDown}
          className="text-neutral-500 hover:text-neutral-700 disabled:opacity-30"
          title={t("editor.preset.moveDown")}
        >
          ↓
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="text-error-dark hover:text-error-dark"
          title={t("common.delete")}
        >
          ✕
        </button>
      </div>

      <div className="mb-1.5">
        <BilingualInput
          value={preset.label ?? ""}
          onChange={(label) => onUpdate({ label })}
        />
      </div>

      <KindFields
        compute={preset.compute}
        onChange={updateCompute}
      />

      <div className="mt-1.5">
        {resolved ? (
          <p className="text-xs text-neutral-500">
            {t("editor.preset.resolves")}{" "}
            <span className="font-mono">
              {resolved.start} → {resolved.end}
            </span>{" "}
            ({resolved.days}d)
          </p>
        ) : resolveError ? (
          <p className="text-xs text-error-dark">
            ⚠ {resolveError}
          </p>
        ) : (
          <p className="text-xs italic text-neutral-500">{t("editor.preset.resolving")}</p>
        )}
      </div>
    </li>
  );
}

interface KindFieldsProps {
  compute: DatePresetCompute;
  onChange: (compute: DatePresetCompute) => void;
}

function KindFields({ compute, onChange }: KindFieldsProps): JSX.Element {
  const { t } = useT();
  return (
    <div className="space-y-1">
      <Select
        value={compute.kind}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          // Picking a new kind resets params to safe starter values.
          // These are EDITABLE seeds visible to the admin, not silent
          // fallbacks — they see them and tune as needed.
          const kind = e.target.value as DatePresetCompute["kind"];
          if (kind === "absolute_range") {
            onChange({
              kind: "absolute_range",
              params: { start: "today", end: "tomorrow" },
            });
          } else if (kind === "relative_days") {
            onChange({ kind: "relative_days", params: { length: 30 } });
          } else if (kind === "relative_months") {
            onChange({ kind: "relative_months", params: { length: 3 } });
          } else if (kind === "hp_fiscal_q") {
            onChange({
              kind: "hp_fiscal_q",
              params: {
                offset_back: 4,
                duration_quarters: 5,
                include_open: true,
              },
            });
          }
        }}
        options={[
          {
            value: "absolute_range",
            label: t("editor.preset.kind.absolute_range"),
          },
          {
            value: "relative_days",
            label: t("editor.preset.kind.relative_days"),
          },
          {
            value: "relative_months",
            label: t("editor.preset.kind.relative_months"),
          },
          { value: "hp_fiscal_q", label: t("editor.preset.kind.hp_fiscal_q") },
        ]}
        size="sm"
        fullWidth
      />

      {compute.kind === "absolute_range" && (
        <div className="grid grid-cols-2 gap-1">
          <input
            value={compute.params.start}
            onChange={(e) =>
              onChange({
                kind: "absolute_range",
                params: { ...compute.params, start: e.target.value },
              })
            }
            className="rounded border border-neutral-300 bg-white px-1.5 py-0.5 font-mono text-xs"
            placeholder="today | 2024-12-03"
          />
          <input
            value={compute.params.end}
            onChange={(e) =>
              onChange({
                kind: "absolute_range",
                params: { ...compute.params, end: e.target.value },
              })
            }
            className="rounded border border-neutral-300 bg-white px-1.5 py-0.5 font-mono text-xs"
            placeholder="tomorrow | 2026-05-25"
          />
        </div>
      )}

      {compute.kind === "relative_days" && (
        <label className="flex items-center gap-1.5 text-xs text-neutral-600">
          {t("editor.preset.lengthDays")}:
          <input
            type="number"
            min={1}
            value={compute.params.length}
            onChange={(e) =>
              onChange({
                kind: "relative_days",
                params: { length: Number(e.target.value) },
              })
            }
            className="w-16 rounded border border-neutral-300 bg-white px-1.5 py-0.5 text-xs"
          />
        </label>
      )}

      {compute.kind === "relative_months" && (
        <label className="flex items-center gap-1.5 text-xs text-neutral-600">
          {t("editor.preset.lengthMonths")}:
          <input
            type="number"
            min={1}
            value={compute.params.length}
            onChange={(e) =>
              onChange({
                kind: "relative_months",
                params: { length: Number(e.target.value) },
              })
            }
            className="w-16 rounded border border-neutral-300 bg-white px-1.5 py-0.5 text-xs"
          />
        </label>
      )}

      {compute.kind === "hp_fiscal_q" && (
        <div className="grid grid-cols-3 gap-1 text-xs text-neutral-600">
          <label className="flex flex-col">
            <span>offset back</span>
            <input
              type="number"
              min={0}
              value={compute.params.offset_back}
              onChange={(e) =>
                onChange({
                  kind: "hp_fiscal_q",
                  params: {
                    ...compute.params,
                    offset_back: Number(e.target.value),
                  },
                })
              }
              className="rounded border border-neutral-300 bg-white px-1 py-0.5"
            />
          </label>
          <label className="flex flex-col">
            <span>duration</span>
            <input
              type="number"
              min={1}
              value={compute.params.duration_quarters}
              onChange={(e) =>
                onChange({
                  kind: "hp_fiscal_q",
                  params: {
                    ...compute.params,
                    duration_quarters: Number(e.target.value),
                  },
                })
              }
              className="rounded border border-neutral-300 bg-white px-1 py-0.5"
            />
          </label>
          <label className="flex items-center gap-1 pt-3">
            <input
              type="checkbox"
              checked={compute.params.include_open}
              onChange={(e) =>
                onChange({
                  kind: "hp_fiscal_q",
                  params: {
                    ...compute.params,
                    include_open: e.target.checked,
                  },
                })
              }
            />
            <span>open</span>
          </label>
        </div>
      )}
    </div>
  );
}
